<template>
    <div class="relative">
        <label class="relative block" ref="reference">
            <input
                class="h-10 w-full flex items-center gap-2 text-left pl-3 text-slate-500 dark:text-slate-400 relative bg-white dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700 py-1.5 placeholder:text-slate-500 dark:text-slate-400 placeholder:text-sm transition-colors text-sm focus:ring-0 focus:border-slate-800 hover:border-slate-800 disabled:opacity-50 disabled:cursor-not-allowed"
                v-model="cardQuery"
                @focus="() => (showCardsList = true)"
                ref="creditCardUsedButton"
            />
            <div
                class="w-full flex items-center gap-2 absolute pr-14 left-2 top-[50%] -translate-y-[calc(50%-0.125rem)] whitespace-nowrap truncate min-w-0 pointer-events-none text-slate-500 dark:text-slate-400 text-sm"
                v-if="
                    (!cardQuery && !showCardsList) ||
                    activeRedemption.connected_account_id
                "
            >
                <template v-if="activeRedemption.connected_account_id">
                    <ImageLoader>
                        <template #image>
                            <img
                                :src="`${cdnUrl}/${connectedAccount?.card?.card_image?.path}/_small/${connectedAccount?.card?.card_image?.file_name}`"
                                :alt="connectedAccount?.card?.title"
                                class="w-8 h-5 rounded-sm"
                            />
                        </template>
                        <template #error>
                            <Icon
                                icon="credit-card"
                                class="text-slate-600 dark:text-slate-300 w-8 h-5"
                            />
                        </template>
                    </ImageLoader>
                    <span class="truncate w-full overflow-hidden text-sm">
                        {{ connectedAccount?.card?.title }}
                        {{
                            connectedAccount?.last_four
                                ? `(${connectedAccount?.last_four})`
                                : ''
                        }}
                    </span>
                </template>
                <template v-else>Credit card used</template>
            </div>
            <button
                class="block leading-none text-red-600 w-5 h-5 absolute right-2 top-1/2 -translate-y-1/2 z-10"
                v-if="activeRedemption.connected_account_id"
                @click="clearCard()"
            >
                <Icon icon="close" />
            </button>
        </label>

        <Teleport to="body">
            <transition name="fade">
                <div
                    class="max-h-48 p-2 overflow-y-auto bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-slate-200 dark:border-slate-700 fixed z-[99999]"
                    v-show="showCardsList"
                    ref="floating"
                    :style="`width: ${width}px; top: ${top}px; left: ${left}px`"
                >
                    <template
                        v-for="walletUser in filteredWalletUsers"
                        :key="walletUser.id"
                    >
                        <p
                            class="flex items-center gap-2 text-slate-500 dark:text-slate-400 text-sm font-medium mb-3"
                        >
                            <Icon icon="user" class="w-5 h-5 text-slate-500 dark:text-slate-400" />
                            {{ walletUser.name }}
                        </p>
                        <div
                            v-for="institution in walletUser.institutions"
                            :key="institution.id"
                        >
                            <p class="text-slate-500 dark:text-slate-400 text-sm mb-1 pt-1">
                                {{
                                    institution.issuing_bank?.name ||
                                    institution.name ||
                                    'Unknown Bank'
                                }}
                            </p>
                            <div class="mb-2 border-b border-slate-200 dark:border-slate-700 pb-2">
                                <label
                                    v-for="account in institution.accounts"
                                    :key="account.id"
                                    class="w-full flex items-center gap-2 px-3 py-2 text-slate-800 dark:text-slate-100 text-sm bg-white dark:bg-slate-800 rounded hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors mb-1 cursor-pointer"
                                    @click="
                                        showCardsList = false;
                                        cardQuery = null;
                                    "
                                >
                                    <input
                                        class="sr-only"
                                        type="radio"
                                        name="connected_account_id"
                                        :value="account.id"
                                        v-model="
                                            activeRedemption.connected_account_id
                                        "
                                    />
                                    <ImageLoader>
                                        <template #image>
                                            <img
                                                :src="`${cdnUrl}/${account?.card?.card_image?.path}/_small/${account?.card?.card_image?.file_name}`"
                                                class="w-8 h-5 rounded-sm"
                                                alt="Card image"
                                            />
                                        </template>
                                        <template #error>
                                            <Icon
                                                icon="credit-card"
                                                class="text-slate-600 dark:text-slate-300 w-8 h-5"
                                            />
                                        </template>
                                    </ImageLoader>
                                    {{ account.card?.title }}
                                    ({{ account.last_four }})
                                </label>
                            </div>
                        </div>
                    </template>
                </div>
            </transition>
        </Teleport>
    </div>
</template>
<script setup>
import { storeToRefs } from 'pinia';
import {
    onMounted,
    onBeforeUnmount,
    ref,
    useTemplateRef,
    watch,
    computed,
} from 'vue';
import ImageLoader from '@/components/ui/image-loader/image-loader.vue';
import { useTripsStore } from '@/store/trips';
import { autoUpdate } from '@floating-ui/vue';
import Icon from '@/components/ui/icon/icon.vue';

const tripsStore = useTripsStore();
const { activeRedemption, connectedAccount, wallet } = storeToRefs(tripsStore);

const cardQuery = ref(null);
const showCardsList = ref(false);
const creditCardUsedButton = useTemplateRef('creditCardUsedButton');
const reference = ref(null);
const floating = ref(null);
const top = ref(0);
const left = ref(0);
const width = ref(80);
let stopAutoUpdate = null;

// Debounce the search to prevent excessive re-renders
const debouncedCardQuery = computed(() => {
    return cardQuery.value;
});

// Use a more efficient filtering approach
const filteredWalletUsers = computed(() => {
    if (!wallet.value?.users) return [];
    if (!debouncedCardQuery.value) return wallet.value.users;

    const query = debouncedCardQuery.value.toLowerCase();
    const results = [];

    for (const user of wallet.value.users) {
        const filteredInstitutions = [];

        for (const institution of user.institutions) {
            const filteredAccounts = institution.accounts.filter(
                (account) =>
                    account.card?.title?.toLowerCase().includes(query) ||
                    account.last_four?.includes(query) ||
                    (institution.issuing_bank?.name || institution.name || '')
                        .toLowerCase()
                        .includes(query)
            );

            if (filteredAccounts.length > 0) {
                filteredInstitutions.push({
                    ...institution,
                    accounts: filteredAccounts,
                });
            }
        }

        if (filteredInstitutions.length > 0) {
            results.push({
                ...user,
                institutions: filteredInstitutions,
            });
        }
    }

    return results;
});

const cdnUrl = computed(() => {
    return window.cdnUrl;
});

const setCardsDropdownPosition = () => {
    if (!creditCardUsedButton.value) {
        return;
    }
    const position = creditCardUsedButton.value.getBoundingClientRect();
    top.value = position.top + 42;
    left.value = position.left;
    width.value = position.width;
};

// While open, keep the teleported list glued to the input across every
// possible cause of movement: modal scroll, page scroll, window resize,
// browser zoom, layout shifts, and input size changes. autoUpdate walks
// the reference's scroll ancestors — unlike a single hard-coded
// `.ui-modal-content` scroll listener, which can bind to the wrong
// hidden modal-content when several stacked modals exist in the DOM.
watch(showCardsList, (val) => {
    if (val) {
        setCardsDropdownPosition();
        if (creditCardUsedButton.value && floating.value) {
            stopAutoUpdate = autoUpdate(
                creditCardUsedButton.value,
                floating.value,
                setCardsDropdownPosition
            );
        }
    } else if (stopAutoUpdate) {
        stopAutoUpdate();
        stopAutoUpdate = null;
    }
});

const clearCard = () => {
    activeRedemption.value.connected_account_id = null;
    cardQuery.value = '';
    showCardsList.value = false;
};

// Teleported dropdown lives outside the trigger's DOM subtree, so a
// document-level v-click-outside on the input treats every touch or click
// inside the floating list as "outside" and closes before selection can
// register (SUPPORT-767). Mirror the transfer-programs-dropdown pattern:
// check both the trigger and the floating list, and use touchend so the
// initial touchstart of a scroll gesture doesn't dismiss the list.
const handleGlobalClose = (event) => {
    if (
        (floating.value && floating.value.contains(event.target)) ||
        (reference.value && reference.value.contains(event.target))
    ) {
        return;
    }

    showCardsList.value = false;
};

const handleEscape = (event) => {
    if (event.key === 'Escape' && showCardsList.value) {
        showCardsList.value = false;
    }
};

onMounted(() => {
    document.addEventListener('click', handleGlobalClose);
    document.addEventListener('touchend', handleGlobalClose);
    document.addEventListener('keydown', handleEscape);
});

onBeforeUnmount(() => {
    if (stopAutoUpdate) {
        stopAutoUpdate();
        stopAutoUpdate = null;
    }
    document.removeEventListener('click', handleGlobalClose);
    document.removeEventListener('touchend', handleGlobalClose);
    document.removeEventListener('keydown', handleEscape);
});
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.5s;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
